import { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import { Spinner } from "react-bootstrap";
import { toast } from "react-hot-toast";

const MAX_IMAGES = 8;
const MAX_FILE_SIZE = 5 * 1024 * 1024;

async function uploadToCloudinary(file) {
  const cloudName = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME || "afrilocales";
  const uploadPreset = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET || "svmprhvf";
  const uploadUrl = `https://api.cloudinary.com/v1_1/${cloudName}/image/upload`;

  const formData = new FormData();
  formData.append("file", file);
  formData.append("upload_preset", uploadPreset);

  const response = await fetch(uploadUrl, { method: "POST", body: formData });
  const data = await response.json();
  if (!response.ok || !data?.secure_url) {
    throw new Error(data?.error?.message || "Upload failed");
  }
  return data.secure_url;
}

function normalizeMediaItem(item) {
  if (!item) return null;
  if (typeof item === "string") return { url: item, type: "image" };
  const url = item.url || item.src || item.secure_url;
  if (!url) return null;
  return { url, type: item.type || "image", alt: item.alt || "" };
}

/**
 * Multi-image uploader for marketplace products.
 * First image is used as cover_image; all images go into media[].
 */
export default function MarketplaceProductImageUploader({
  media = [],
  onChange,
  maxImages = MAX_IMAGES,
}) {
  const [uploading, setUploading] = useState(false);
  const items = (media || []).map(normalizeMediaItem).filter(Boolean);

  const emit = (next) => {
    onChange(next.map(normalizeMediaItem).filter(Boolean));
  };

  const handleFiles = useCallback(
    async (files) => {
      const remaining = maxImages - items.length;
      if (remaining <= 0) {
        toast.error(`You can upload up to ${maxImages} images.`);
        return;
      }

      const accepted = [];
      for (const file of files.slice(0, remaining)) {
        if (!file.type?.startsWith("image/")) {
          toast.error(`${file.name} is not an image.`);
          continue;
        }
        if (file.size > MAX_FILE_SIZE) {
          toast.error(`${file.name} is larger than 5MB.`);
          continue;
        }
        accepted.push(file);
      }
      if (!accepted.length) return;

      setUploading(true);
      try {
        const urls = [];
        for (const file of accepted) {
          // Upload sequentially to keep Cloudinary rate limits predictable
          // eslint-disable-next-line no-await-in-loop
          const url = await uploadToCloudinary(file);
          urls.push({ url, type: "image" });
        }
        emit([...items, ...urls]);
        toast.success(
          urls.length === 1 ? "Image uploaded" : `${urls.length} images uploaded`
        );
      } catch (error) {
        toast.error(error?.message || "Image upload failed. Please try again.");
      } finally {
        setUploading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [items, maxImages]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleFiles,
    accept: { "image/*": [".jpeg", ".jpg", ".png", ".gif", ".webp"] },
    multiple: true,
    disabled: uploading || items.length >= maxImages,
  });

  const removeAt = (index) => {
    emit(items.filter((_, i) => i !== index));
  };

  const setAsCover = (index) => {
    if (index === 0) return;
    const next = [...items];
    const [picked] = next.splice(index, 1);
    next.unshift(picked);
    emit(next);
  };

  return (
    <div>
      <p className="text-muted small mb-2">
        Upload one or more product images (JPG, PNG, WebP, max 5MB each). The first image is
        used as the cover.
      </p>

      <div
        {...getRootProps()}
        className="border rounded-3 p-3 mb-3 text-center"
        style={{
          cursor: uploading || items.length >= maxImages ? "not-allowed" : "pointer",
          borderStyle: "dashed",
          background: isDragActive ? "rgba(139, 49, 58, 0.04)" : "#fafafa",
          opacity: uploading || items.length >= maxImages ? 0.7 : 1,
        }}
      >
        <input {...getInputProps()} />
        {uploading ? (
          <div className="d-flex align-items-center justify-content-center gap-2 py-2">
            <Spinner size="sm" animation="border" />
            <span className="small">Uploading…</span>
          </div>
        ) : (
          <div className="py-2">
            <div className="fw-semibold small mb-1">
              {isDragActive ? "Drop images here" : "Drag & drop images, or click to browse"}
            </div>
            <div className="text-muted small">
              {items.length}/{maxImages} images · single or multiple selection
            </div>
          </div>
        )}
      </div>

      {items.length > 0 ? (
        <div className="row g-2">
          {items.map((item, index) => (
            <div key={`${item.url}-${index}`} className="col-6 col-md-4 col-lg-3">
              <div
                className="position-relative border rounded-3 overflow-hidden bg-light"
                style={{ aspectRatio: "1 / 1" }}
              >
                <img
                  src={item.url}
                  alt={item.alt || `Product image ${index + 1}`}
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
                {index === 0 ? (
                  <span
                    className="badge position-absolute top-0 start-0 m-1"
                    style={{ backgroundColor: "#8b313a" }}
                  >
                    Cover
                  </span>
                ) : null}
                <div className="position-absolute bottom-0 start-0 end-0 p-1 d-flex gap-1 justify-content-center flex-wrap"
                  style={{ background: "rgba(0,0,0,0.45)" }}
                >
                  {index !== 0 ? (
                    <button
                      type="button"
                      className="btn btn-sm btn-light py-0 px-1"
                      style={{ fontSize: 11 }}
                      onClick={() => setAsCover(index)}
                    >
                      Set cover
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className="btn btn-sm btn-danger py-0 px-1"
                    style={{ fontSize: 11 }}
                    onClick={() => removeAt(index)}
                  >
                    Remove
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-muted small mb-0">No product images yet.</p>
      )}
    </div>
  );
}

export function mediaToCoverImage(media = []) {
  const first = normalizeMediaItem(media?.[0]);
  return first?.url || null;
}
